import React, { useState, useEffect } from 'react';
import { useLoader } from '../../../contexts/LoaderContext';  // Import the loader context
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  Link,
  CircularProgress,
  } from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAgentReport } from '../../../contexts/AgentReportContext';
import {getRandomAvatarColor} from '../../../utils/helper';

const TeamLeaderboard = ({ dateRangeState, periodType }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { fetchAgentLeaderBoard, loading, error } = useAgentReport();
  const { showLoader, hideLoader } = useLoader();
  const [leaderboardData, setLeaderboardData] = useState([]);

  useEffect(() => {
    showLoader();
    fetchData();
    hideLoader();
  }, [dateRangeState, periodType]);

  const fetchData = async () => {
    showLoader();
    try {
      const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
      const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
      const data = await fetchAgentLeaderBoard(periodType, startDate, endDate);
      setLeaderboardData(data);
    } catch (error) {
      console.error('Error fetching leaderboard data:', error);
      setLeaderboardData([]);
    } finally {
      hideLoader();
    }
  };  

  // Handler for agent click
  const handleAgentClick = (agentId) => {
    navigate(`/reports/agent/${agentId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Typography variant="body2" color="error">Error loading leaderboard data: {error.message || 'Request failed with status code 404'}</Typography>
      </Box>
    );
  }

  const mockLeaderboardData = [
    {
      agent: 'Meena',
      agentId: 1,
      team: 'Alpha',
      leadsAssigned: 156,
      totalCalls: 210,
      newLeads: 60,
      followUps: 150,
      responseTime: '22m',
      siteVisits: 12,
      taskPercent: '94%',
      bookings: 4,
      dealValue: '₹1.2Cr',
      avatar: 'M',
      performance: 'excellent',
    },
    {
      agent: 'Ramesh',
      agentId: 2,
      team: 'Beta',
      leadsAssigned: 142,
      totalCalls: 175,
      newLeads: 50,
      followUps: 125,
      responseTime: '35m',
      siteVisits: 10,
      taskPercent: '89%',
      bookings: 3,
      dealValue: '₹0.9Cr',
      avatar: 'R',
      performance: 'good',
    },
    {
      agent: 'Raj',
      agentId: 3,
      team: 'Alpha',
      leadsAssigned: 98,
      totalCalls: 98,
      newLeads: 40,
      followUps: 58,
      responseTime: '1h12m',
      siteVisits: 3,
      taskPercent: '58%',
      bookings: 0,
      dealValue: '₹0',
      avatar: 'R',
      performance: 'needs_improvement',
    },
  ];

  const getPerformanceColor = (performance) => {
    switch (performance) {
      case 'excellent':
        return 'success';
      case 'good':
        return 'info';
      case 'needs_improvement':
        return 'warning';
      default:
        return 'primary'; // Use 'primary' as it always has a .main property
    }
  };

  const getPerformanceIcon = (performance) => {
    switch (performance) {
      case 'excellent':
        return <TrendingUpIcon sx={{ fontSize: '1rem' }} />;
      case 'good':
        return <TrendingUpIcon sx={{ fontSize: '1rem' }} />;
      case 'needs_improvement':
        return <TrendingDownIcon sx={{ fontSize: '1rem' }} />;
      default:
        return null;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        background: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: 'text.primary',
            }}
          >
            Team Leaderboard
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Export Data">
              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Search Agents">
              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                <SearchIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: '0.875rem',
          }}
        >
          Agent performance comparison
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: alpha(theme.palette.grey[50], 0.5),
                '& .MuiTableCell-head': {
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: 'text.primary',
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                },
              }}
            >
              <TableCell>Agent</TableCell>
              <TableCell align="center">Leads Assigned</TableCell>
              <TableCell align="center">Total Calls</TableCell>
              <TableCell align="center">Fresh Calls</TableCell>
              <TableCell align="center">Follow-ups Calls</TableCell>
              <TableCell align="center">Not Connected Calls</TableCell>
              <TableCell align="center">Total Talk Time</TableCell>
              <TableCell align="center">Site Visits</TableCell>
              <TableCell align="center">FollowUp %</TableCell>
              <TableCell align="center">Bookings</TableCell>
              <TableCell align="center">Deal Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaderboardData.map((agent, index) => {
              // Map API response fields to component expected fields
              const mappedAgent = {
                agent: agent.agent || 'Unknown',
                agentId: agent.agent_id || agent.agentId || index,
                team: agent.team || 'No Team',
                leadsAssigned: agent.assigned_leads !== undefined ? agent.assigned_leads 
                  : agent.leads_assigned !== undefined ? agent.leads_assigned 
                  : agent.leadsAssigned !== undefined ? agent.leadsAssigned 
                  : (agent.new_calls || 0) + (agent.follow_up_calls || 0),
                totalCalls: agent.total_calls || agent.totalCalls || 0,
                newLeads: agent.new_calls || agent.new_leads || agent.newLeads || 0,
                followUps: agent.follow_up_calls || agent.follow_ups || agent.followUps || 0,
                notConnectedCalls: agent.not_connected_calls !== undefined ? agent.not_connected_calls : 0,
                responseTime: agent.response_time || agent.responseTime || '0m 0s',
                totalTalkTime: agent.total_talk_time || agent.totalTalkTime || '0m 0s',
                siteVisits: agent.site_visits || agent.siteVisits || 0,
                taskPercent: agent.followup_completion_percentage !== undefined 
                  ? `${agent.followup_completion_percentage}%` 
                  : agent.task_completion_percentage !== undefined
                  ? `${agent.task_completion_percentage}%`
                  : agent.task_percent || agent.taskPercent || '0%',
                bookings: agent.count_of_bookings || agent.bookings || 0,
                dealValue: agent.deal_value_display || agent.deal_value || agent.dealValue || '₹0',
                avatar: agent.initials || agent.avatar || '?',
                performance: agent.performance || 'medium'
              };
              
              // Calculate task percentage numeric value for color determination
              const taskPercentValue = parseFloat(String(mappedAgent.taskPercent).replace('%', ''));
              
              return (
              <TableRow
                key={index}
                sx={{
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                  },
                  '& .MuiTableCell-body': {
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                    fontSize: '0.875rem',
                  },
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: getRandomAvatarColor(),
                        color: 'white',
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {mappedAgent.avatar}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="body2"
                        component="span"
                        onClick={() => handleAgentClick(mappedAgent.agentId)}
                        sx={{
                          fontWeight: 600,
                          color: 'primary.main',
                          cursor: 'pointer',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: 'primary.dark',
                          },
                        }}
                      >
                        {mappedAgent.agent}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          display: 'block',
                          mt: 0.5,
                        }}
                      >
                        {mappedAgent.team}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {mappedAgent.leadsAssigned}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {mappedAgent.totalCalls}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {mappedAgent.newLeads}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {mappedAgent.followUps}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {mappedAgent.notConnectedCalls}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {mappedAgent.totalTalkTime}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {mappedAgent.siteVisits}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: taskPercentValue >= 90 ? 'success.main' : taskPercentValue >= 70 ? 'warning.main' : 'error.main',
                    }}
                  >
                    {mappedAgent.taskPercent}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {mappedAgent.bookings}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: mappedAgent.dealValue === '₹0' ? 'text.secondary' : 'success.main',
                    }}
                  >
                    {mappedAgent.dealValue}
                  </Typography>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TeamLeaderboard;
